import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  DATE_RANGE_PICKER_CONFIG,
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
  TABLE_TOOLBAR_CONFIG,
  getCandidatesFilterKeys,
} from "./CandidatesPageConfig";

import type { FilterSectionConfig } from "@/components/ui/v2/filters/FiltersDrawer";
import type { ActiveFilter } from "@/components/ui/v2/table/table-active-filters";
import { URL_PARAMS } from "@/constants/urlParams";
import type { UseApplyFiltersParams } from "@/features/integrations/modules/applies/api";
import {
  buildAppliesFilterParams,
  getFilterSections,
  getValueToLabel,
} from "@/features/integrations/modules/applies/pages/overview/appliesFilterHelpers";
import {
  useCandidateFilters,
  useCandidates,
  useCandidatesMetrics,
  type CandidatesMetricsResponse,
  type CandidateType,
} from "@/features/integrations/modules/candidates/api";
import type { RenderContext } from "@/features/integrations/shared/renderer/types";
import { useAuth } from "@/lib/hooks/useAuth";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import {
  formatLocalDateOnly,
  localStartOfDayUtc,
  localEndOfDayUtc,
  parseLocalDateOnly,
} from "@/utils/dateUtils";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export type UseCandidatesPageOptions = {
  clientId?: string;
};

/**
 * The metric fields are the response shape itself (nulls and all) rather than a hand-copy, so the
 * cards can't drift from what the endpoint returns — only `undefined` (no data yet) is coalesced away.
 */
export type UseCandidatesPageResult = CandidatesMetricsResponse & {
  context: RenderContext;
  clientScoped: boolean;
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;
  filterSections: FilterSectionConfig[];
  activeFilters: ActiveFilter[];
  removeFilter: (f: ActiveFilter) => void;
  clearFilters: () => void;
  filtersLoading: boolean;
  totalRecords: number;
  /** Everything a preview count query needs except the pending filter selection itself. */
  countBaseParams: {
    clientId?: string;
    tenantId: string | null;
    search?: string;
    startDate: string;
    endDate: string;
  };
  filterKeys: readonly string[];
  /** Scope for the drawer's paged option queries — the same one its option list is drawn from. */
  filterOptionParams: UseApplyFiltersParams;
  metricsLoading: boolean;
  searchParams: ReturnType<typeof useSearchParams>[0];
};

export function useCandidatesPage(
  options?: UseCandidatesPageOptions,
): UseCandidatesPageResult {
  const clientId = options?.clientId;
  const { context: authContext } = useAuth();
  // Client-context users (Managers, Spectators) are always client-scoped, even without a clientId in
  // the URL — the EF global query filter restricts their data.
  const clientScoped = !!clientId || authContext === "client";
  const filterKeys = getCandidatesFilterKeys(clientScoped);
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenantId } = useAuth();
  const { orderBy: rawOrderBy, descending: rawDescending } = useSortSearchParams();
  const orderBy = rawOrderBy ?? "totalApplies";
  const descending = rawDescending ?? true;
  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const { searchValue, debouncedSearchValue, setSearchValue } =
    useSearchSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Reset filters, search, dates, and pagination when tenant switches — none of these carry over
  // between tenants. Mirrors the Applies page reset.
  const [prevTenantId, setPrevTenantId] = useState(tenantId);
  useEffect(() => {
    if (prevTenantId === tenantId) return;
    setPrevTenantId(tenantId);
    // Skip the null → value transition on initial auth hydration — that's not a user-triggered switch.
    if (prevTenantId == null || tenantId == null) return;
    const next = new URLSearchParams(searchParams);
    filterKeys.forEach((k) => next.delete(k));
    next.delete(URL_PARAMS.SEARCH);
    next.delete("startDate");
    next.delete("endDate");
    next.set(URL_PARAMS.PAGE, "1");
    setSearchParams(next, { replace: true });
    setSearchValue("");
  }, [tenantId, prevTenantId, searchParams, setSearchParams, filterKeys, setSearchValue]);

  const startDate = searchParams.get("startDate")?.trim() || undefined;
  const endDate = searchParams.get("endDate")?.trim() || undefined;

  useEffect(() => {
    if (!searchParams.get("startDate") && !searchParams.get("endDate")) {
      const next = new URLSearchParams(searchParams);
      next.set("startDate", formatLocalDateOnly(DEFAULT_START_DATE));
      next.set("endDate", formatLocalDateOnly(DEFAULT_END_DATE));
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Centralized filter params — single source of truth for all hooks.
  const filterParams = useMemo(() => {
    const params = buildAppliesFilterParams(searchParams, filterKeys);
    // Client-scoped page: lock clients to the single client from the URL.
    if (clientId) params.clients = [clientId];
    return params;
  }, [searchParams, filterKeys, clientId]);

  // URL stores local date strings (YYYY-MM-DD) for display; the API needs UTC ISO strings so filters
  // match UTC-stored dates.
  const effectiveStartLocal = parseLocalDateOnly(startDate) ?? DEFAULT_START_DATE;
  const effectiveEndLocal = parseLocalDateOnly(endDate) ?? DEFAULT_END_DATE;
  const effectiveStartDate = localStartOfDayUtc(effectiveStartLocal);
  const effectiveEndDate = localEndOfDayUtc(effectiveEndLocal);

  // --- Data hooks (all share the same filterParams) ---

  const {
    data: candidates,
    isLoading,
    isFetching,
  } = useCandidates({
    ...filterParams,
    page,
    pageSize,
    search: debouncedSearchValue ?? undefined,
    tenantId,
    startDate: effectiveStartDate,
    endDate: effectiveEndDate,
    sortDirection: descending ? "desc" : "asc",
    sortColumn: orderBy,
  });

  // Filter options only depend on date range + tenant — not on active filter selections. Counted in
  // candidates by the Candidates FilterCounts endpoint, so the option numbers match this table's rows.
  // The drawer's paged dimensions load on scroll from the same scope, so it is shared with them.
  const filterOptionParams = useMemo(
    () => ({
      clients: clientId ? [clientId] : undefined,
      search: debouncedSearchValue ?? undefined,
      startDate: effectiveStartDate,
      endDate: effectiveEndDate,
      tenantId,
    }),
    [clientId, debouncedSearchValue, effectiveStartDate, effectiveEndDate, tenantId],
  );

  const { data: filterData, isLoading: filtersLoading } =
    useCandidateFilters(filterOptionParams);

  const countBaseParams = useMemo(
    () => ({
      clientId,
      tenantId,
      search: debouncedSearchValue ?? undefined,
      startDate: effectiveStartDate,
      endDate: effectiveEndDate,
    }),
    [clientId, tenantId, debouncedSearchValue, effectiveStartDate, effectiveEndDate],
  );

  const {
    data: metrics,
    isLoading: metricsLoading,
    isFetching: metricsFetching,
  } = useCandidatesMetrics({
    ...filterParams,
    search: debouncedSearchValue ?? undefined,
    tenantId,
    startDate: effectiveStartDate,
    endDate: effectiveEndDate,
  });

  const handleSort = useTableSortHandler<CandidateType>();

  // --- URL param helpers ---

  const updateParams = useCallback(
    (opts: { deleteKeys?: string[]; set?: Record<string, string[]> }) => {
      const next = new URLSearchParams(searchParams);
      opts.deleteKeys?.forEach((k) => next.delete(k));
      if (opts.set) {
        Object.entries(opts.set).forEach(([k, vals]) => {
          next.delete(k);
          vals.forEach((v) => next.append(k, v));
        });
      }
      next.set(URL_PARAMS.PAGE, "1");
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const setDateRange = useCallback(
    (from?: Date, to?: Date) => {
      updateParams({
        set: {
          startDate: from ? [formatLocalDateOnly(from)] : [],
          endDate: to ? [formatLocalDateOnly(to)] : [],
        },
      });
    },
    [updateParams],
  );

  const onFilterChange = useCallback(
    (key: string, value: unknown) => {
      if (
        key !== TABLE_TOOLBAR_CONFIG.dateRangeFilterKey ||
        value == null ||
        typeof value !== "object"
      )
        return;
      const range = value as { startDate?: string; endDate?: string };
      const from = range.startDate ? new Date(range.startDate) : undefined;
      const to = range.endDate ? new Date(range.endDate) : undefined;
      setDateRange(from, to);
    },
    [setDateRange],
  );

  const filterValues = useMemo(
    () => ({
      [TABLE_TOOLBAR_CONFIG.dateRangeFilterKey]: {
        startDate:
          searchParams.get("startDate") ?? formatLocalDateOnly(DEFAULT_START_DATE),
        endDate:
          searchParams.get("endDate") ?? formatLocalDateOnly(DEFAULT_END_DATE),
      },
    }),
    [searchParams],
  );

  // --- Derived state ---

  const filterSections = useMemo(
    () => getFilterSections(filterData, filterKeys, t),
    [filterData, filterKeys, t],
  );
  const valueToLabel = useMemo(
    () => getValueToLabel(filterData, filterKeys, t),
    [filterData, filterKeys, t],
  );

  const activeFilters = useMemo((): ActiveFilter[] => {
    return filterKeys.flatMap((key) =>
      searchParams.getAll(key).map((v) => ({
        key,
        label: t(`common.${key}`),
        value: v,
        valueLabel: valueToLabel[key]?.[v] ?? v,
      })),
    );
  }, [searchParams, t, valueToLabel, filterKeys]);

  const removeFilter = useCallback(
    (f: ActiveFilter) => {
      const current = searchParams.getAll(f.key).filter((v) => v !== String(f.value));
      updateParams({ set: { [f.key]: current } });
    },
    [searchParams, updateParams],
  );

  const clearFilters = useCallback(() => {
    updateParams({ deleteKeys: [...filterKeys] });
  }, [updateParams, filterKeys]);

  const totalRecords = candidates?.totalRecords ?? 0;
  const hasFilters =
    !!searchValue ||
    filterKeys.some((key) => searchParams.getAll(key).length > 0);

  // Flatten client.name into clientName for column accessor compatibility.
  const tableData = useMemo(
    () =>
      (candidates?.data ?? []).map((row) => ({
        ...row,
        clientName: row.client?.name ?? "",
      })),
    [candidates?.data],
  );

  const context = useMemo<RenderContext>(
    () => ({
      integrationName: "candidates",
      moduleName: "candidates",
      tenantId: tenantId ?? undefined,
      clientId: clientId ?? undefined,
      executeAction: async () => {},
      tableData,
      isLoading: isLoading || isFetching,
      pagination: {
        page,
        pageSize,
        totalRecords,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
      },
      // Pass the raw (URL-derived) sort state so the header only shows a sort icon after the user
      // clicks; the API call above uses the effective orderBy/descending (totalApplies-desc default).
      orderBy: rawOrderBy,
      descending: rawDescending,
      onSort: handleSort as RenderContext["onSort"],
      emptyMessage: hasFilters
        ? t("common.noResultsFound")
        : t("integrations.cockpit.candidates.noCandidates"),
      searchValue: searchValue ?? "",
      onSearchChange: setSearchValue,
      filterValues,
      onFilterChange,
      dateRangePickerOptions: {
        maxDate: new Date(),
        ...DATE_RANGE_PICKER_CONFIG,
      },
    }),
    [
      clientId,
      tenantId,
      tableData,
      isLoading,
      isFetching,
      page,
      pageSize,
      totalRecords,
      setPage,
      setPageSize,
      rawOrderBy,
      rawDescending,
      handleSort,
      hasFilters,
      t,
      searchValue,
      setSearchValue,
      filterValues,
      onFilterChange,
    ],
  );

  return {
    context,
    clientScoped,
    filtersOpen,
    setFiltersOpen,
    filterSections,
    activeFilters,
    removeFilter,
    clearFilters,
    filtersLoading,
    totalRecords,
    countBaseParams,
    filterKeys,
    filterOptionParams,
    metricsLoading: metricsLoading || metricsFetching,
    totalCandidates: metrics?.totalCandidates ?? 0,
    totalApplies: metrics?.totalApplies ?? 0,
    // Ratios stay null when the backend has nothing to divide by — the cards render that as N/A.
    averageAppliesPerCandidate: metrics?.averageAppliesPerCandidate ?? null,
    qualifiedCount: metrics?.qualifiedCount ?? 0,
    qualifiedPercentage: metrics?.qualifiedPercentage ?? null,
    hireableCount: metrics?.hireableCount ?? 0,
    hireablePercentage: metrics?.hireablePercentage ?? null,
    searchParams,
  };
}
